import {
  CATEGORY_SHOTGUN,
  CATEGORY_RIFLE,
  CATEGORY_FIREARMS,
  CATEGORY_FIREARMS_MODULE_NAMES,
  CATEGORY_AMMUNITION,
  CATEGORY_AMMUNITION_MODULE_NAMES,
  CATEGORY_HANDGUN,
  CATEGORY_RIMFIRE,
  CATEGORY_SHOTSHELL,
  CATEGORY_SUPPRESSORS,
  CATEGORY_SUPPRESSORS_MODULE_NAMES,
  CATEGORY_OTHERS_MODULE_NAMES,
  CATEGORY_OPTICS,
  CATEGORY_OPTICS_MODULE_NAMES,
  CATEGORY_OBSERVATION,
  CATEGORY_NIGHT_VISION,
  CATEGORY_THERMAL,
  CATEGORY_OPTICS_ACCESSORIES,
  CATEGORY_ACCESSORIES,
  CATEGORY_ACCESSORIES_MODULE_NAMES,
  CATEGORY_ACCESSORIES_CUTLERY,
  CATEGORY_ACCESSORIES_GUN_CLEANING,
  CATEGORY_ACCESSORIES_GUN_PARTS,
  CATEGORY_ACCESSORIES_GUN_SAFETY_STORAGE,
  CATEGORY_ACCESSORIES_HOLSTERS,
  CATEGORY_ACCESSORIES_MAGAZINES,
  CATEGORY_ACCESSORIES_SHOOTING_ACCESSORIES,
} from 'pages/product-full-view/components/product-new-detail-view/constants';

export const filterProductSchemaByRules = (
  productSchema,
  basicInformationData,
) => {
  const subCategory = basicInformationData?.SubCategory?.toLowerCase() || '';

  return productSchema.map((module) => {
    // Filter Construction module for Shotgun category
    if (module.moduleName === 'ConstructionFirearms') {
      return {
        ...module,
        moduleProperties: module.moduleProperties.map((property) => {
          // Remove MultiChoke if category is not Shotgun
          if (property.propertyName === 'ConstructionBarrel') {
            const isShotgun = subCategory === CATEGORY_SHOTGUN?.toLowerCase();
            const isRifle = subCategory === CATEGORY_RIFLE?.toLowerCase();

            return {
              ...property,
              childProperties: property.childProperties.filter((child) => {
                if (
                  child.propertyName === 'MultiChoke' ||
                  child.propertyName === 'Choke' ||
                  child.propertyName === 'RifledBarrel'
                ) {
                  return isShotgun;
                }

                if (child.propertyName === 'TwistRate') {
                  return isShotgun || isRifle;
                }

                return true;
              }),
            };
          }
          return property;
        }),
      };
    }

    // Filter Attributes module for Black Powder subcategory
    if (
      module.moduleName === 'Attributes' ||
      module.moduleName === 'AttributesFirearms'
    ) {
      const isBlackPowder = subCategory === 'black powder';
      return {
        ...module,
        moduleProperties: module.moduleProperties.filter((property) => {
          // Remove BlackPowderCaliberGauge if subcategory is not Black Powder
          if (property.propertyName === 'BlackPowderCaliberGauge') {
            return isBlackPowder;
          }
          return true;
        }),
      };
    }

    return module;
  });
};

export const getChildPropertiesByFieldFullPath = (
  properties,
  fieldFullPath,
) => {
  if (!properties || !Array.isArray(properties)) {
    return [];
  }

  for (const property of properties) {
    // Check if current property matches the fieldFullPath
    if (
      property.fieldFullPath === fieldFullPath ||
      property.fieldFullPath.includes(fieldFullPath)
    ) {
      return property.childProperties || [];
    }

    // Recursively search in childProperties
    if (property.childProperties && property.childProperties.length > 0) {
      const result = getChildPropertiesByFieldFullPath(
        property.childProperties,
        fieldFullPath,
      );
      if (result.length > 0) {
        return result;
      }
    }
  }

  return [];
};

export const findChildPropertiesInSchema = (productSchema, fieldFullPath) => {
  if (!productSchema || !Array.isArray(productSchema)) {
    return [];
  }

  for (const module of productSchema) {
    if (module.moduleProperties && module.moduleProperties.length > 0) {
      const result = getChildPropertiesByFieldFullPath(
        module.moduleProperties,
        fieldFullPath,
      );
      if (result.length > 0) {
        return result;
      }
    }
  }

  return [];
};

export const initializeAccessoriesOpticsIfNeeded = (
  formValues,
  moduleName,
  productSchema,
) => {
  // Only process if module is Accessories
  if (moduleName !== 'Accessories') {
    return formValues;
  }

  const opticReady = formValues?.OpticReady;
  const accessoriesOptics = formValues?.AccessoriesOptics;

  // If OpticReady is true and AccessoriesOptics is null/undefined, initialize it
  if (
    opticReady === true &&
    (accessoriesOptics === null || accessoriesOptics === undefined)
  ) {
    const accessoriesOpticsChildProperties = findChildPropertiesInSchema(
      productSchema,
      'AccessoriesOptics',
    );

    const initialAccessoriesOptics = {};
    accessoriesOpticsChildProperties.forEach((childProperty) => {
      initialAccessoriesOptics[childProperty.propertyName] = null;
    });

    return {
      ...formValues,
      AccessoriesOptics: initialAccessoriesOptics,
    };
  }

  // Return formValues unchanged if conditions are not met
  return formValues;
};

const normalize = (value) => value?.toLowerCase();

export const filterModuleNamesByCategoryAndSubcategory = (
  moduleNames = [],
  category,
  subCategory,
) => {
  const normalizedCategory = normalize(category);
  const normalizedSubCategory = normalize(subCategory);

  // --- FIREARMS ---
  if (normalizedCategory === normalize(CATEGORY_FIREARMS)) {
    const allowedModules = new Set(
      CATEGORY_FIREARMS_MODULE_NAMES.map(normalize),
    );

    return moduleNames.filter((moduleItem) =>
      allowedModules.has(normalize(moduleItem?.moduleName)),
    );
  }

  // --- AMMUNITION ---
  if (normalizedCategory === normalize(CATEGORY_AMMUNITION)) {
    const allowedModules = new Set(
      CATEGORY_AMMUNITION_MODULE_NAMES.map(normalize),
    );

    let result = moduleNames.filter((moduleItem) =>
      allowedModules.has(normalize(moduleItem?.moduleName)),
    );

    const constructionModuleBySubCategory = {
      [normalize(CATEGORY_HANDGUN)]: 'AttributesAmmunitionHandgun',
      [normalize(CATEGORY_RIFLE)]: 'AttributesAmmunitionRifle',
      [normalize(CATEGORY_RIMFIRE)]: 'AttributesAmmunitionRimfire',
      [normalize(CATEGORY_SHOTSHELL)]: 'AttributesAmmunitionShotshell',
    };

    const constructionModuleName =
      constructionModuleBySubCategory[normalizedSubCategory];

    if (constructionModuleName) {
      const constructionModule = moduleNames.find(
        (moduleItem) => moduleItem?.moduleName === constructionModuleName,
      );

      if (constructionModule) {
        result = [constructionModule, ...result];
      }
    }

    return result;
  }

  // --- SUPPRESSORS ---
  if (normalizedCategory === normalize(CATEGORY_SUPPRESSORS)) {
    const allowedModules = new Set(
      CATEGORY_SUPPRESSORS_MODULE_NAMES.map(normalize),
    );

    return moduleNames.filter((moduleItem) =>
      allowedModules.has(normalize(moduleItem?.moduleName)),
    );
  }

  //
  if (normalizedCategory === normalize(CATEGORY_OPTICS)) {
    const allowedModules = new Set(CATEGORY_OPTICS_MODULE_NAMES.map(normalize));

    let result = moduleNames.filter((moduleItem) =>
      allowedModules.has(normalize(moduleItem?.moduleName)),
    );

    const attributesModuleBySubCategory = {
      [normalize(CATEGORY_HANDGUN)]: 'AttributesOpticsHandgun',
      [normalize(CATEGORY_RIFLE)]: 'AttributesOpticsRifle',
      [normalize(CATEGORY_OBSERVATION)]: 'AttributesOpticsObservation',
      [normalize(CATEGORY_NIGHT_VISION)]: 'AttributesOpticsNightVision',
      [normalize(CATEGORY_THERMAL)]: 'AttributesOpticsThermal',
      [normalize(CATEGORY_OPTICS_ACCESSORIES)]:
        'AttributesOpticsOpticsAccessories',
    };

    const attributeModuleName =
      attributesModuleBySubCategory[normalizedSubCategory];

    if (attributeModuleName) {
      const attributeModule = moduleNames.find(
        (moduleItem) => moduleItem?.moduleName === attributeModuleName,
      );

      if (attributeModule) {
        result = [attributeModule, ...result];
      }
    }

    return result;
  }

  if (normalizedCategory === normalize(CATEGORY_ACCESSORIES)) {
    const allowedModules = new Set(
      CATEGORY_ACCESSORIES_MODULE_NAMES.map(normalize),
    );

    let result = moduleNames.filter((moduleItem) =>
      allowedModules.has(normalize(moduleItem?.moduleName)),
    );

    const attributesModuleBySubCategory = {
      [normalize(CATEGORY_ACCESSORIES_CUTLERY)]: 'AttributesAccessoriesCutlery',
      [normalize(CATEGORY_ACCESSORIES_GUN_CLEANING)]:
        'AttributesAccessoriesGunCleaning',
      [normalize(CATEGORY_ACCESSORIES_GUN_PARTS)]:
        'AttributesAccessoriesGunParts',
      [normalize(CATEGORY_ACCESSORIES_GUN_SAFETY_STORAGE)]:
        'AttributesAccessoriesGunSafetyStorage',
      [normalize(CATEGORY_ACCESSORIES_HOLSTERS)]:
        'AttributesAccessoriesHolsters',
      [normalize(CATEGORY_ACCESSORIES_MAGAZINES)]:
        'AttributesAccessoriesMagazines',
      [normalize(CATEGORY_ACCESSORIES_SHOOTING_ACCESSORIES)]:
        'AttributesAccessoriesShootingAccessories',
    };

    const attributeModuleName =
      attributesModuleBySubCategory[normalizedSubCategory];

    if (attributeModuleName) {
      const attributeModule = moduleNames.find(
        (moduleItem) => moduleItem?.moduleName === attributeModuleName,
      );

      if (attributeModule) {
        result = [attributeModule, ...result];
      }
    }

    return result;
  }

  // Others
  const allowedModules = new Set(CATEGORY_OTHERS_MODULE_NAMES.map(normalize));

  return moduleNames.filter((moduleItem) =>
    allowedModules.has(normalize(moduleItem?.moduleName)),
  );
};
